import { BillingPeriod, BillingType, SERVICE_TYPE } from "providers/Auth";
import { MagicLink } from "./address";
import {
  IntegrationCredential,
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types";
import { BillingConfiguration } from "./billing-configuration";

export enum FEATURES {
  FREE_CHECKOUT = "free_checkout",
  SDK = "sdk",
  DELINQUENCY_REPORTING = "delinquency_reporting",
  API_ACCESS = "api_access",
  BOOM_SCREEN = "boom_screen",
  BOOM_CRM = "boom_crm",
  PROVISIONED_PHONE_NUMBER = "provisioned_phone_number",
  CRM_FEATURE_WORKFLOWS = "crm_feature_workflows",
  WORKFLOWS = "workflows",
  COMMUNICATION_CAMPAIGNS = "communication_campaigns",
  TENANT_LEADS = "tenant_leads",
  TASKS = "tasks",
  LOCKBOXES = "lockboxes",
  UI_MASTER_SEARCH = "ui_master_search",
  UI_STRIPE_BANK_VERIFICATION = "ui_stripe_bank_verification",
  SHOWINGS = "showings",
  MARKET_RENT = "market_rent",
  OWNER_REPORTS = "owner_reports",
  SYNDICATION = "syndication",
  CRM_LISTING_SYNC = "crm_listing_sync",
  COMMUNICATION_EMAIL = "communication_email",
  CUSTOMIZE_LEAD_SOURCES_STAGES = "customize_lead_sources_stages",
  RENT_REPORTING = "rent_reporting",
  SITES_AND_COMMUNITIES = "sites_and_communities",
  SHOW_FULL_SSN = "show_full_ssn",
  USE_PROPERTY_NAME_IN_EMAIL = "use_property_name_in_email",
  PROPERTY_GROUP_MARKET = "property_group_market",
  USE_PROPERTY_NAME_IN_SUBMISSION_DISCLOSURE = "use_property_name_in_submission_disclosure",
  ENTERPRISE_PROPERTYWARE = "enterprise_propertyware",
  MANUAL_APPLICATION_PROPERTY_MANAGER_SUBMISSION_DISCLOSURE = "manual_application_property_manager_submission_disclosure",
  AI_APPLICATION_SUMMARY = "ai_application_summary",
  TRANSUNION_CREDIT_REPORT = "transunion_credit_report",
  PMS_LISTING_SYNC = "pms_listing_sync",
  ENABLE_CREDIT_IMPACT_DASHBOARD = "enable_credit_impact_dashboard",
  HOME_SITE_RENT = "home_site_rent",
  WEEKLY_BILLING = "weekly_billing",
  SCHEDULING = "scheduling",
  TEMPLATE_FEE_COLLECTION = "template_fee_collection",
  TEMPLATE_ROUTING = "template_routing",
  USE_PROPERTYWARE_MULTIMEDIA_SCRAPER = "use_propertyware_multimedia_scraper",
  WIP_FEAT_PREMIUM_RENTAL_VERIFICATION = "wip_feat_premium_rental_verification",
  COMMUNICATION = "communication",
  COMMUNICATION_SMS = "communication_sms",
  NATIVE_CHAT = "native_chat",
  COMMUNICATION_PHONE = "communication_phone",
  ENABLE_IDENTITY_THEFT_PROTECTION = "enable_identity_theft_protection",
  USE_SLIM_APPLICATIONS_API = "use_slim_applications_api",
  EXTENDED_BANK_STATEMENTS = "extended_bank_statements",
  STRIPE_MANAGED_BILLING = "stripe_managed_billing",
  MULTIPLE_PROPERTY_MANAGEMENT_SYSTEMS = "multiple_property_management_systems",
  COMBINED_SEARCH_V2 = "combined_search_v2",
  ENGLISH_ONLY = "english_only",
  WIP_BOOM_FINANCE = "wip_boom_finance",
  BOOM_SCREEN_OUTCOMES = "boom_screen_outcomes",
  CRM_AI_VOICE_AGENT = "crm_ai_voice_agent",
  CRM_AI_LEAD_SCORING = "crm_ai_lead_scoring",
  OWNER_LEADS = "owner_leads",
  CRM_ANALYTICS = "crm_analytics",
  ARGYLE_PAYROLL = "argyle_payroll",
  UI_BOOM_SCREEN_ANALYTICS = "ui_boom_screen_analytics",
  ONBOARDING_CHECKLIST = "onboarding_checklist",
  ADDRESS_VALIDATION = "address_validation",
  UI_ONBOARDING_V2 = "ui_onboarding_v2",
  /**
   * Mirrors `Boom::Partner::FEATURES`. The partner-wide entitlement / kill
   * switch for AppFolio dynamic pricing: `Partner#dynamic_pricing_active_for?`
   * requires it *and* the per-credential `appfolio_dynamic_pricing` setting
   * (admin-only) before any matrix is surfaced or applied. Matrices keep being
   * ingested either way, so a unit having a matrix no longer implies it is
   * dynamically priced. Nothing in the portal gates on this yet.
   */
  PMS_DYNAMIC_PRICING = "pms_dynamic_pricing",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag), not a plan entitlement: it gates
   * visibility of the read-only Collections "Outstanding balance" page (BF-454)
   * while the screen is still being iterated on. The endpoint behind it is not
   * gated — it returns the partner's own residents, as /leases already does.
   */
  UI_COLLECTIONS_OUTSTANDING_BALANCE = "ui_collections_outstanding_balance",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag): the Boom products page (PLT-725) — the Explore new
   * products nav button, /products with self-serve Enable, and the Products notifications tab. The API
   * gates the same endpoints on it, partner-wide only, so target partners rather than users.
   */
  UI_BOOM_PRODUCTS = "ui_boom_products",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag): picks which endpoint the
   * Outstanding balance list reads. ON serves it from Boom's own tables
   * (`/collections/outstanding_balances`); OFF proxies to the integration
   * service on every page load, which is still the default.
   *
   * The response shape is identical either way, but coverage is not: the local
   * list omits residents the daily sweep has not entered — newly overdue on the
   * 1st or 2nd, in a filed eviction, or exited while still owing. Turn it on per
   * partner, not globally.
   */
  UI_OUTSTANDING_BALANCE_V2 = "ui_outstanding_balance_v2",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag), not a plan entitlement: it
   * picks which analytics page renders (PLT-578). ON gives the merged Leasing
   * dashboard; OFF keeps the "BoomScreen" and "BoomCRM" tabs it replaced.
   * See `views/analytics/leasingDashboardFlag.ts`.
   */
  UI_LEASING_DASHBOARD = "ui_leasing_dashboard",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag), not a plan entitlement: it
   * holds Shareable applicant reports (BSCPF-6078) back while the feature ships
   * dark. OFF
   * hides the Settings → Features card, so a partner cannot switch the feature
   * on, and hides the surfaces that would follow from it.
   *
   * Distinct from `owner_screening_reports`, which is the partner's own switch
   * once they are allowed to see it. See `shared/hooks/use-owner-reports-flag`.
   */
  UI_SCREENING_OWNER_REPORTS = "ui_screening_owner_reports",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag), not a plan entitlement: it
   * gates the premium warning report while it is still work in progress.
   * Nothing in the portal gates on this yet.
   */
  UI_WIP_PREMIUM_WARNING_REPORT = "ui_wip_premium_warning_report",
  /**
   * Mirrors `Boom::Partner::FEATURES`. Gates the verified landlord contacts
   * work for premium rental history (BSCPF-6730): the nightly suggestion job
   * and the survey-time verified-contact checks. Off for every partner by
   * default. Nothing in the portal gates on this yet.
   */
  VERIFIED_LANDLORD_CONTACTS = "verified_landlord_contacts",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag), not a plan entitlement: it gates
   * the Employer verification card inside a screening template's services
   * (BSCPF-6584), so the service can ship dark and be switched on per partner
   * from admin's /ui-feature-flags page with no client deploy.
   *
   * Front-end only. The API lists the `employment_verification` schema and acts on
   * it regardless — the flag decides who can *see and toggle* the card, not whether
   * a template that already carries the service runs it.
   * See `shared/app/VerificationTemplate/employerVerificationFlag.ts`.
   */
  UI_EMPLOYER_VERIFICATION = "ui_employer_verification",
  /**
   * Mirrors `Boom::Partner::FEATURES`. The partner's entitlement to AI
   * underwriting agents (BSCPF-6141): without it the API leaves them out of
   * the agents list and refuses to create, read or change one.
   * See `shared/app/UnderwritingAgents/underwritingAgentsFlag.ts`.
   */
  AI_UNDERWRITING_AGENT = "ai_underwriting_agent",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag), not a plan entitlement: it
   * hides the underwriting agents UI (BSCPF-6141) while it ships in parts.
   * See `shared/app/UnderwritingAgents/underwritingAgentsFlag.ts`.
   */
  UI_UNDERWRITING_AGENT = "ui_underwriting_agent",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag): next/previous application
   * navigation (BSCPF-6998) while it ships dark.
   * See `views/applications-id/hooks/use-application-navigation-flag.ts`.
   */
  UI_APPLICATION_NAVIGATION = "ui_application_navigation",
  /**
   * Mirrors `Boom::Partner::FEATURES`. Turns on Yardi floorplans (BSCPF-6960):
   * the API sends `floorplan` on units and the portal shows and edits it.
   */
  MULTI_FAMILY = "multi_family",
  /**
   * A `ui_` registry flag (Boom::UiFeatureFlag): the resident site (PLT-967)
   * while it rolls out per partner.
   *
   * Gates the partner-facing surfaces only — Settings → Resident site, and
   * anywhere the portal links a partner user to their residents' site. The
   * resident-facing route itself (`/r/:partnerSlug`) is public, so it has no
   * partner in context to read this from and is gated server-side instead:
   * `site_config` 404s when the site is off.
   * See `shared/hooks/use-resident-site-flag.ts`.
   */
  UI_RESIDENT_SITE = "ui_resident_site"
}

type BankAccount = {
  id: number;
  account_number: string;
  routing_number: string;
  account_nickname: string;
  account_subtype: "checking" | "savings";
  default: boolean;
  name: string;
  last4?: string;
};

export type UloopPricing = "lead" | "featured";

export type NotificationPropertyScope = "all" | "selected";

export type NotificationPropertyFilter = {
  scope: NotificationPropertyScope;
  properties: { id: number; name: string | null }[];
  property_groups: { id: number; name: string }[];
};

export type Partner = {
  name: string;
  url?: string;
  logo_url: string | null;
  algolia: {
    app_id?: string;
    client_search_key?: string;
    index_name?: string;
  } | null;
  billing_period: BillingPeriod;
  pending_billing_period?: BillingPeriod;
  pending_billing_period_effective_at?: string;
  billing_type?: BillingType;
  features: FEATURES[];
  /**
   * Entitlement plan; "legacy" (or absent) means ops never assigned one and
   * entitlements resolve from the raw feature array.
   */
  plan?: "legacy" | "core" | "pro" | "custom" | null;
  service_type?: SERVICE_TYPE;
  property_management_system?: {
    name: PropertyManagementSystem | "";
    last_synced_at: string | null;
  };
  integration_credentials?: IntegrationCredential[];
  magic_link?: MagicLink;
  invoice_billing_day: number;
  payout_billing_day: number;
  payout_bank_account: BankAccount;
  invoice_bank_account: BankAccount;
  opt_out_period_days: number;
  opt_in_period_days: number;
  unexpired_opt_in_period: boolean;
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
    application_group_submitted?: boolean;
    applicant_submitted_application?: boolean;
    rental_history_verification_completed: boolean;
    idv_bypass_requested?: boolean;
  };
  partner_features: PartnerFeatures[];
  can_update_billing_type?: boolean;
  listing_url: string;
  /** Listing Fraud Alerts has no feature flag: it is on once Property Shield is connected. */
  property_shield_connected?: boolean;
  calendar_account_limit?: number | null;
  connected_calendar_account_count?: number | null;
  phone_number_limit?: number | null;
  provisioned_phone_number_count?: number | null;
  // Whether a usable number actually exists for each channel. Not the same as
  // the `provisioned_phone_number` entitlement, which can be granted with none
  // bought — gate send actions on these.
  has_voice_number?: boolean;
  has_sms_number?: boolean;
  workflow_limit?: number | null;
  auto_enrollment_default?: string | null;
  auto_enrollment_enabled_date?: string | null;
  enrollment_eligibility_date?: string | null;
  hubspot_hub_id?: string;
  codebox_connected?: boolean;
  sentrilock_connected?: boolean;
  igloohome_connected?: boolean;
  rently_lockboxes_connected?: boolean;
  zillow_connected?: boolean;
  zumper_connected?: boolean;
  dwellsy_connected?: boolean;
  affordable_housing_com_connected?: boolean;
  rental_beast_connected?: boolean;
  uloop_connected?: boolean;
  uloop_pricing_model?: UloopPricing | null;
  rental_source_connected?: boolean;
  rent_duck_connected?: boolean;
  realtor_com_connected?: boolean;
  colors?: Record<string, Record<string, string>>;
  apartments_com_connected?: boolean;
  mhvillage_connected?: boolean;
  boom_finance_external_id?: string | null;
  appfolio_supergood_enabled?: boolean;
  billing_configuration: BillingConfiguration | null;
  property_type?:
    | "single_family"
    | "multi_family"
    | "manufactured_housing"
    | null;
  preferred_identity_verification_vendor?: "plaid" | "persona" | null;
  similar_unit_outreach_ever_enabled?: boolean;
};
